import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertTriangle, ArrowLeft, Save, Camera, Trash2, Plus, RefreshCw, FileText, Star, Download } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { ThemeSelector } from '@/components/ThemeSelector'
import { api } from '@/lib/api'
import { formatDate, formatFileSize, getStatusColor, getStatusText, buildAuthorizedUrl } from '@/lib/utils'
import type { ApiError, BookAsset, BookDetail } from '@/types'

const editSchema = z.object({
  title: z.string().min(1, '书名不能为空'),
  author: z.string().optional(),
  isbn: z.string().optional(),
  summary: z.string().optional(),
  tags: z.string().optional(),
  themes: z.array(z.string()).optional(),
  status: z.enum(['published', 'unlisted', 'archived']),
  requiredLevel: z.coerce.number().int().min(0, '访问等级必须是非负整数'),
})

type EditFormValues = z.infer<typeof editSchema>

interface AssetUploadState {
  file: File | null
  error: string
  uploading: boolean
}

const assetMimeMap: Record<'epub' | 'pdf', string> = {
  epub: 'application/epub+zip',
  pdf: 'application/pdf',
}

const resolveAssetFormat = (file: File): 'epub' | 'pdf' | null => {
  const lowerName = file.name.toLowerCase()
  if (lowerName.endsWith('.epub')) return 'epub'
  if (lowerName.endsWith('.pdf')) return 'pdf'
  return null
}

export function BookEditPage() {
  const { bookId } = useParams<{ bookId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [submitError, setSubmitError] = useState('')
  const [selectedThemes, setSelectedThemes] = useState<string[]>([])
  const [coverUploading, setCoverUploading] = useState(false)
  const coverInputRef = useRef<HTMLInputElement>(null)
  const [activeTab, setActiveTab] = useState<'basic' | 'assets' | 'cover' | 'publish'>('basic')
  const [assetUploadState, setAssetUploadState] = useState<AssetUploadState>({ file: null, error: '', uploading: false })
  const [downloadingAssetId, setDownloadingAssetId] = useState<string | null>(null)
  const assetInputRef = useRef<HTMLInputElement>(null)

  const { data: book, isLoading, error } = useQuery({
    queryKey: ['book', bookId],
    queryFn: () => {
      if (!bookId) throw new Error('缺少书籍 ID')
      return api.getBook(bookId)
    },
    enabled: !!bookId,
  })

  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      title: '',
      author: '',
      isbn: '',
      summary: '',
      tags: '',
      themes: [],
      status: 'unlisted',
      requiredLevel: 100,
    },
  })

  useEffect(() => {
    if (book) {
      const themeIds = book.themes?.map((t) => t.id) || []
      reset({
        title: book.title,
        author: book.author || '',
        isbn: book.isbn || '',
        summary: book.summary || '',
        tags: book.tags?.join(', ') || '',
        themes: themeIds,
        status: book.status,
        requiredLevel: book.requiredLevel ?? 100,
      })
      setSelectedThemes(themeIds)
    }
  }, [book, reset])

  const invalidateBook = () => {
    if (bookId) {
      queryClient.invalidateQueries({ queryKey: ['book', bookId] })
    }
  }

  const setPrimaryMutation = useMutation({
    mutationFn: ({ assetId }: { assetId: string }) => {
      if (!bookId) throw new Error('缺少书籍 ID')
      return api.updateAsset(bookId, assetId, { isPrimary: true })
    },
    onSuccess: invalidateBook,
  })

  const deleteAssetMutation = useMutation({
    mutationFn: ({ assetId }: { assetId: string }) => {
      if (!bookId) throw new Error('缺少书籍 ID')
      return api.deleteAsset(bookId, assetId)
    },
    onSuccess: invalidateBook,
  })

  const updateMutation = useMutation({
    mutationFn: (payload: EditFormValues) => {
      if (!bookId) throw new Error('缺少书籍 ID')
      const tags = payload.tags ? payload.tags.split(',').map((tag) => tag.trim()).filter(Boolean) : []

      const normalize = (value: string | undefined) => {
        if (value === undefined) return undefined
        return value.trim()
      }

      return api.updateBook(bookId, {
        title: payload.title.trim(),
        author: normalize(payload.author),
        isbn: normalize(payload.isbn),
        summary: normalize(payload.summary),
        status: payload.status,
        tags,
        themes: payload.themes || [],
        requiredLevel: payload.requiredLevel,
      })
    },
    onSuccess: () => {
      invalidateBook()
      navigate(`/books/${bookId}`)
    },
    onError: (err: unknown) => {
      const apiError = err as ApiError
      const message = apiError?.message || '保存失败，请稍后再试'
      setSubmitError(message)
    },
  })

  const onSubmit = (values: EditFormValues) => {
    setSubmitError('')
    return updateMutation.mutateAsync({ ...values, themes: selectedThemes })
  }

  const formatAssets: Array<[string, BookAsset[]]> = book?.assets
    ? (Object.entries(book.assets) as Array<[string, BookAsset[]]>)
    : []

  const tabs: Array<{ id: 'basic' | 'assets' | 'cover' | 'publish'; label: string }> = [
    { id: 'basic', label: '基础信息' },
    { id: 'assets', label: '资产管理' },
    { id: 'cover', label: '封面管理' },
    { id: 'publish', label: '发布设置' },
  ]

  const handleAssetFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    setAssetUploadState({ file, error: '', uploading: false })
  }

  const handleAssetUpload = async () => {
    if (!bookId) return
    const file = assetUploadState.file
    if (!file) {
      setAssetUploadState((prev) => ({ ...prev, error: '请选择要上传的文件' }))
      return
    }

    const format = resolveAssetFormat(file)
    if (!format) {
      setAssetUploadState((prev) => ({ ...prev, error: '不支持的文件格式' }))
      return
    }

    const expectedType = assetMimeMap[format]
    if (file.type && file.type !== expectedType) {
      setAssetUploadState((prev) => ({ ...prev, error: `文件类型应为 ${expectedType}` }))
      return
    }

    if (file.size > 200 * 1024 * 1024) {
      setAssetUploadState((prev) => ({ ...prev, error: '文件大小不能超过 200MB' }))
      return
    }

    setAssetUploadState((prev) => ({ ...prev, uploading: true, error: '' }))

    try {
      const initResult = await api.initUpload(bookId, {
        format,
        filename: file.name,
        contentType: file.type,
      })

      await api.uploadFile(bookId, file, format, initResult.r2Key)

      const isPrimaryForFormat = !formatAssets.some(([existingFormat]) => existingFormat === format)
      await api.registerAsset(bookId, {
        format,
        r2Key: initResult.r2Key,
        filename: file.name,
        sizeBytes: file.size,
        contentType: file.type,
        isPrimary: isPrimaryForFormat,
      })

      setAssetUploadState({ file: null, error: '', uploading: false })
      if (assetInputRef.current) {
        assetInputRef.current.value = ''
      }
      invalidateBook()
    } catch (err) {
      const apiError = err as ApiError
      const message = apiError?.message || '上传失败，请稍后再试'
      setAssetUploadState((prev) => ({ ...prev, uploading: false, error: message }))
    }
  }

  const handleDeleteAsset = async (assetId: string) => {
    if (!confirm('确定要删除该资产吗？此操作无法撤销。')) return
    try {
      await deleteAssetMutation.mutateAsync({ assetId })
    } catch (err) {
      console.error('删除资产失败:', err)
      alert('删除资产失败，请稍后重试')
    }
  }

  const handleDownloadAsset = async (assetId: string) => {
    if (!bookId) return
    setDownloadingAssetId(assetId)
    try {
      const { downloadUrl } = await api.getAssetDownloadUrl(bookId, assetId)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      console.error('下载资产失败:', err)
      alert('下载文件失败，请稍后重试')
    } finally {
      setDownloadingAssetId(null)
    }
  }

  const handleCoverSelection = async (file: File) => {
    if (!bookId) return

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('只支持 JPG、PNG、WebP 格式的图片')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('图片文件不能超过 5MB')
      return
    }

    setCoverUploading(true)
    try {
      const response = await api.uploadCover(bookId, file)
      queryClient.setQueryData(['book', bookId], (prev: BookDetail | undefined) =>
        prev ? { ...prev, coverUrl: response.coverUrl } : prev,
      )
      invalidateBook()
    } catch (err) {
      console.error('封面上传失败:', err)
      alert('封面上传失败')
    } finally {
      setCoverUploading(false)
      if (coverInputRef.current) {
        coverInputRef.current.value = ''
      }
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">加载书籍信息...</h1>
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            正在加载，请稍候
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !book) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="h-5 w-5" />
          <h1 className="text-2xl font-bold">无法加载书籍信息</h1>
        </div>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />返回
        </Button>
      </div>
    )
  }

  const coverImageUrl = buildAuthorizedUrl(book.coverUrl) ?? book.coverUrl ?? undefined;
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">编辑书籍</h1>
            <p className="mt-1 text-sm text-gray-500">
              {book.title} ·
              <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(book.status)}`}>
                {getStatusText(book.status)}
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate(`/books/${book.id}`)}>
              <ArrowLeft className="mr-2 h-4 w-4" />返回详情
            </Button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? 'bg-primary-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {activeTab === 'basic' && (
          <section className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">基本信息</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      label="书名 *"
                      {...register('title')}
                      error={errors.title?.message}
                      placeholder="输入书籍标题"
                      disabled={isSubmitting}
                    />
                  </div>

                  <Input
                    label="作者"
                    {...register('author')}
                    error={errors.author?.message}
                    placeholder="输入作者姓名"
                    disabled={isSubmitting}
                  />

                  <Input
                    label="ISBN"
                    {...register('isbn')}
                    error={errors.isbn?.message}
                    placeholder="978-x-xxx-xxxxx-x"
                    disabled={isSubmitting}
                  />

                  <Input
                    label="访问等级"
                    type="number"
                    min={0}
                    {...register('requiredLevel')}
                    error={errors.requiredLevel?.message}
                    helper="数字越小权限越高，普通用户建议设置为 100"
                    disabled={isSubmitting}
                  />
                </div>

                <Textarea
                  label="简介"
                  rows={4}
                  {...register('summary')}
                  error={errors.summary?.message}
                  placeholder="输入书籍简介，帮助读者了解书籍内容..."
                  disabled={isSubmitting}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">分类与标签</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">主题分类</label>
                  <ThemeSelector
                    value={selectedThemes}
                    onChange={(themes) => {
                      setSelectedThemes(themes)
                      setValue('themes', themes)
                    }}
                  />
                </div>

                <Input
                  label="标签"
                  {...register('tags')}
                  error={errors.tags?.message}
                  placeholder="用逗号分隔，如：科幻, 小说, 经典"
                  disabled={isSubmitting}
                  helper="标签用于更细致的分类和搜索"
                />
              </CardContent>
            </Card>
          </section>
        )}

        {activeTab === 'assets' && (
          <section>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">资产管理</CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={invalidateBook}
                  disabled={deleteAssetMutation.isPending || setPrimaryMutation.isPending || assetUploadState.uploading}
                  title="刷新资产列表"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {formatAssets.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-500 mb-4">暂无资产文件</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {formatAssets.map(([format, assets]) => (
                      <div key={format} className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-700">
                          {format.toUpperCase()} 文件 ({assets.length})
                        </h3>
                        {assets.map((asset) => (
                          <div
                            key={asset.id}
                            className="flex items-center justify-between p-3 rounded-lg border bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium">
                                  {asset.filename || '未命名文件'}
                                </span>
                                {asset.isPrimary && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                    <Star className="h-3 w-3" />主文件
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1 ml-6">
                                {formatFileSize(asset.sizeBytes || 0)} · 上传于 {formatDate(asset.createdAt)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadAsset(asset.id)}
                                disabled={downloadingAssetId === asset.id}
                                title="下载文件"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {!asset.isPrimary && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPrimaryMutation.mutate({ assetId: asset.id })}
                                  disabled={setPrimaryMutation.isPending}
                                  title="设为主文件"
                                >
                                  <Star className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAsset(asset.id)}
                                disabled={deleteAssetMutation.isPending}
                                title="删除文件"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">添加新文件</h4>
                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <input
                      ref={assetInputRef}
                      type="file"
                      accept=".pdf,.epub"
                      onChange={handleAssetFileChange}
                      className="flex-1 text-sm text-gray-600 file:mr-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50"
                    />
                    <Button
                      type="button"
                      onClick={handleAssetUpload}
                      disabled={!assetUploadState.file || assetUploadState.uploading}
                      loading={assetUploadState.uploading}
                      size="sm"
                    >
                      <Plus className="mr-1 h-3 w-3" />上传
                    </Button>
                  </div>
                  {assetUploadState.error && (
                    <p className="text-xs text-red-600 mt-2">{assetUploadState.error}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">支持 PDF 和 EPUB 格式，最大 200MB</p>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {activeTab === 'cover' && (
          <section>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">封面管理</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-4">
                <div className="relative group">
                  {coverImageUrl ? (
                    <div className="w-48 aspect-[2/3] overflow-hidden rounded-lg border">
                      <img src={coverImageUrl} alt="书籍封面" className="h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => coverInputRef.current?.click()}
                          disabled={coverUploading}
                        >
                          <Camera className="h-4 w-4" />更换
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          onClick={async () => {
                            if (!bookId) return
                            if (confirm('确定要删除封面吗？')) {
                              try {
                                setCoverUploading(true)
                                await api.deleteCover(bookId)
                                invalidateBook()
                              } catch (err) {
                                alert('删除封面失败')
                              } finally {
                                setCoverUploading(false)
                              }
                            }
                          }}
                          disabled={coverUploading}
                        >
                          <Trash2 className="h-4 w-4" />删除
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-48 aspect-[2/3] flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-sm text-gray-400 hover:border-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => coverInputRef.current?.click()}
                    >
                      <div className="text-center p-4">
                        <Camera className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                        <p className="font-medium">上传封面</p>
                        <p className="text-xs mt-1">点击选择图片</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500 text-center space-y-1">
                  <p>JPG、PNG、WebP</p>
                  <p>建议 300×450 像素</p>
                  <p>最大 5MB</p>
                </div>
                <input
                  ref={coverInputRef}
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  disabled={coverUploading}
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (!file) return
                    handleCoverSelection(file)
                  }}
                />
              </CardContent>
            </Card>
          </section>
        )}

        {activeTab === 'publish' && (
          <section className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">发布设置</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="status-select">
                  图书状态
                </label>
                <select
                  id="status-select"
                  {...register('status')}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  disabled={isSubmitting}
                >
                  <option value="published">已发布</option>
                  <option value="unlisted">未发布</option>
                  <option value="archived">已归档</option>
                </select>
                <p className="text-xs text-gray-500">
                  发布前请确保至少存在一个主资产文件。
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">元数据概览</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500">ID：</span>
                  <span className="font-mono text-xs">{book.id}</span>
                </div>
                <div>
                  <span className="text-gray-500">创建时间：</span>
                  <span>{formatDate(book.createdAt)}</span>
                </div>
                <div>
                  <span className="text-gray-500">更新时间：</span>
                  <span>{formatDate(book.updatedAt)}</span>
                </div>
                <div>
                  <span className="text-gray-500">资产数量：</span>
                  <span>{book.assetCount} 个文件</span>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {submitError && (
          <div className="rounded-md bg-red-50 border border-red-200 p-4">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pb-6">
          <Button type="button" variant="secondary" size="lg" onClick={() => navigate(`/books/${book.id}`)} disabled={isSubmitting}>
            取消
          </Button>
          <Button type="submit" size="lg" loading={isSubmitting}>
            <Save className="mr-2 h-4 w-4" />保存修改
          </Button>
        </div>
      </form>
    </div>
  )
}

export default BookEditPage
